/**
 * Workshop Types settings list (BON-117; reconciled to the dashboards-era
 * codebase in BON-391).
 *
 * Reads the catalogue through the SHARED react-query key, and every write here
 * invalidates it — so a type edited in Settings shows up in the create drawer's
 * type picker and the Basics combobox straight away rather than after their
 * `staleTime` expires.
 *
 * Write affordances gate on WRITE_WORKSHOPS, which mirrors what the API accepts
 * today (`write:workshops` / `write:all`). That gate is the wrong shape for a
 * global, region-less catalogue under ADR-0016 — moving it is a tracked
 * follow-up, not this issue.
 */
import { useQuery } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  fetchWorkshopTypes,
  WORKSHOP_TYPES_QUERY_KEY,
  WORKSHOP_TYPES_STALE_MS,
  type WorkshopType,
} from '../../../api/workshop-types';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

import { useWorkshopTypeWrites } from './useWorkshopTypeWrites';
import WorkshopTypeList from './WorkshopTypeList';

export default function WorkshopTypesPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.WRITE_WORKSHOPS);
  const writes = useWorkshopTypeWrites();

  const { data, isLoading, isError } = useQuery({
    queryKey: WORKSHOP_TYPES_QUERY_KEY,
    queryFn: fetchWorkshopTypes,
    staleTime: WORKSHOP_TYPES_STALE_MS,
  });
  const types = data ?? [];

  const confirmDelete = (type: WorkshopType) => {
    const label = type.displayName ?? type.name;
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    writes.remove(type);
  };

  return (
    <div className="bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-stone-200 dark:border-stone-700">
        <Layers size={16} className="text-moss-600 dark:text-moss-400" />
        <h2 className="text-base font-semibold text-bark-900 dark:text-stone-100">
          Workshop Types
        </h2>
        <span className="text-xs text-stone-400 ml-1 mr-auto">{types.length} types</span>
        {canEdit && (
          <button
            onClick={() => navigate('/admin/settings/workshop-types/new')}
            className="text-xs px-2.5 py-1 rounded-md bg-moss-600 text-white hover:bg-moss-700"
          >
            New type
          </button>
        )}
      </div>

      <div className="p-5">
        {writes.error && (
          <div
            role="alert"
            className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm"
          >
            {writes.error}
          </div>
        )}
        <WorkshopTypeList
          types={types}
          isLoading={isLoading}
          isError={isError}
          canEdit={canEdit}
          busy={writes.writing}
          onOpen={(type) => navigate(`/admin/settings/workshop-types/${type.id}`)}
          onToggleActive={writes.toggleActive}
          onDelete={confirmDelete}
        />
      </div>
    </div>
  );
}
